<template>
    <div class="soporte-page">
        <!-- Page Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span>
                    <span class="current">Soporte y Descarga</span>
                </nav>
                <h1 class="page-title">Soporte Técnico</h1>
                <p class="page-subtitle">
                    Estamos aquí para ayudarte. Encuentra respuestas o contáctanos directamente.
                </p>
            </div>
        </section>

        <!-- Content -->
        <section class="soporte-content">
            <div class="container">
                <!-- Contact Cards -->
                <div class="contact-cards">
                    <div class="contact-card" v-if="config.phones && config.phones.length">
                        <div class="card-icon phone">
                            <i class="fas fa-phone"></i>
                        </div>
                        <h3>Llámanos</h3>
                        <p>Lunes a Viernes: 9:00 AM - 6:00 PM</p>
                        <div class="contact-info">
                            <a 
                                v-for="phone in config.phones" 
                                :key="phone.id"
                                :href="`tel:${phone.phone}`"
                                class="contact-link"
                            >
                                {{ phone.phone }}
                            </a>
                        </div>
                    </div>
                    
                    <div class="contact-card" v-if="config.emails && config.emails.length">
                        <div class="card-icon email">
                            <i class="fas fa-envelope"></i>
                        </div>
                        <h3>Escríbenos</h3>
                        <p>Responderemos en 24 horas</p>
                        <div class="contact-info">
                            <a 
                                v-for="email in config.emails" 
                                :key="email.id"
                                :href="`mailto:${email.email}`"
                                class="contact-link"
                            >
                                {{ email.email }}
                            </a>
                        </div>
                    </div>
                    
                    <div class="contact-card" v-if="config.whatsapp_agents && config.whatsapp_agents.length">
                        <div class="card-icon whatsapp">
                            <i class="fab fa-whatsapp"></i>
                        </div>
                        <h3>WhatsApp</h3>
                        <p>Atención inmediata</p>
                        <div class="contact-info">
                            <a 
                                v-for="agent in config.whatsapp_agents" 
                                :key="agent.id"
                                :href="`https://wa.me/${cleanPhoneNumber(agent.phone)}?text=${encodeURIComponent(agent.welcome_message || 'Hola, necesito soporte técnico')}`"
                                target="_blank"
                                class="contact-link"
                            >
                                {{ agent.name }}
                            </a>
                        </div>
                    </div>
                </div>
                
                <!-- FAQ Section -->
                <section class="faq-section">
                    <h2 class="section-title">Preguntas Frecuentes</h2>
                    <p class="section-subtitle">Encuentra respuestas rápidas a las dudas más comunes</p>
                    
                    <div v-if="loading" class="loading">
                        <div class="spinner"></div>
                        <p>Cargando preguntas frecuentes...</p>
                    </div>

                    <div v-else-if="faqs.length" class="faq-grid">
                        <div 
                            v-for="(faq, index) in faqs" 
                            :key="faq.id"
                            class="faq-item"
                            :class="{ 'open': activeFaq === index }"
                            @click="toggleFaq(index)"
                        >
                            <div class="faq-question">
                                <div class="question-content">
                                    <span v-if="faq.categoria" class="faq-category">
                                        {{ faq.categoria }}
                                    </span>
                                    <h4>{{ faq.pregunta }}</h4>
                                </div>
                                <i class="fas fa-chevron-down"></i>
                            </div>
                            <transition name="slide">
                                <div v-show="activeFaq === index" class="faq-answer">
                                    <div v-html="faq.respuesta"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <div v-else class="no-faqs">
                        <i class="fas fa-question-circle"></i>
                        <p>No hay preguntas frecuentes disponibles en este momento</p>
                    </div>
                </section>
                
                <!-- Resources Section -->
                <section class="resources-section">
                    <h2 class="section-title">Recursos Útiles</h2>
                    <p class="section-subtitle">Accede a herramientas y documentación</p>
                    
                    <div class="resources-grid">
                        <router-link to="/drivers-descargas" class="resource-card">
                            <div class="resource-icon">
                                <i class="fas fa-download"></i>
                            </div>
                            <h3>Drivers y Descargas</h3>
                            <p>Encuentra y descarga los drivers de todos nuestros productos</p>
                        </router-link>
                        
                        <router-link to="/blog" class="resource-card">
                            <div class="resource-icon">
                                <i class="fas fa-book"></i>
                            </div>
                            <h3>Guías y Tutoriales</h3>
                            <p>Aprende a configurar y utilizar nuestros productos</p>
                        </router-link>
                        
                        <router-link to="/videos" class="resource-card">
                            <div class="resource-icon">
                                <i class="fas fa-video"></i>
                            </div>
                            <h3>Videos Tutoriales</h3>
                            <p>Mira nuestros videos paso a paso</p>
                        </router-link>
                        
                        <router-link to="/contacto" class="resource-card">
                            <div class="resource-icon">
                                <i class="fas fa-headset"></i>
                            </div>
                            <h3>Contacto Directo</h3>
                            <p>Envíanos un mensaje y te responderemos pronto</p>
                        </router-link>
                    </div>
                </section>
            </div>
        </section>
    </div>
</template>

<script>
import api from '../services/api';
import { mapGetters } from 'vuex';

export default {
    name: 'Soporte',
    data() {
        return {
            faqs: [],
            activeFaq: null,
            loading: true,
            config: {
                phones: [],
                emails: [],
                whatsapp_agents: []
            }
        };
    },
    computed: {
        ...mapGetters(['configuration'])
    },
    async created() {
        await this.loadFaqs();
        this.loadConfig();
    },
    methods: {
        async loadFaqs() {
            try {
                this.loading = true;
                const response = await api.getFaqs();
                this.faqs = response.data || [];
                this.loading = false;
            } catch (error) {
                console.error('Error loading FAQs:', error);
                this.loading = false;
            }
        },
        loadConfig() {
            if (this.configuration) {
                this.config = {
                    phones: this.configuration.phones || [],
                    emails: this.configuration.emails || [],
                    whatsapp_agents: this.configuration.whatsapp_agents || []
                };
            }
        },
        toggleFaq(index) {
            this.activeFaq = this.activeFaq === index ? null : index;
        },
        cleanPhoneNumber(phone) {
            if (!phone) return '';
            return phone.replace(/\D/g, '');
        }
    },
    watch: {
        configuration() {
            this.loadConfig();
        }
    }
};
</script>

<style scoped>
.soporte-page {
    background-color: var(--background);
    min-height: 100vh;
    padding-top: 80px;
}

/* PAGE HEADER */
.page-header {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    padding: 60px 2rem;
    position: relative;
}

.page-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.1), transparent 60%);
}

.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
}

/* BREADCRUMBS */
.breadcrumbs {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
}

.breadcrumbs a {
    color: var(--text-gray);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-gold);
}

.breadcrumbs .separator {
    color: rgba(212, 175, 55, 0.3);
}

.breadcrumbs .current {
    color: var(--accent-gold);
    font-weight: 600;
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-light);
}

.page-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
}

/* CONTENT */
.soporte-content {
    padding: 60px 2rem;
}

/* CONTACT CARDS */
.contact-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 5rem;
}

.contact-card {
    background: var(--card-bg);
    padding: 2.5rem;
    border-radius: 16px;
    border: 1px solid rgba(212, 175, 55, 0.1);
    text-align: center;
    transition: all 0.3s ease;
}

.contact-card:hover {
    transform: translateY(-5px);
    border-color: var(--accent-gold);
    box-shadow: 0 10px 40px rgba(212, 175, 55, 0.2);
}

.card-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: var(--text-light);
}

.card-icon.phone {
    background: linear-gradient(135deg, var(--accent-teal), var(--primary-blue));
}

.card-icon.email {
    background: linear-gradient(135deg, var(--primary-blue), var(--primary-dark));
}

.card-icon.whatsapp {
    background: linear-gradient(135deg, #25D366, #128C7E);
}

.contact-card h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--accent-gold);
    margin-bottom: 0.5rem;
}

.contact-card p {
    color: var(--text-gray);
    margin-bottom: 1.5rem;
}

.contact-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.contact-link {
    color: var(--accent-teal);
    font-weight: 600;
    text-decoration: none;
    transition: color 0.3s ease;
}

.contact-link:hover {
    color: var(--accent-gold);
}

/* FAQ SECTION */
.faq-section,
.resources-section {
    margin: 5rem 0;
}

.section-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, var(--text-light), var(--accent-gold));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section-subtitle {
    text-align: center;
    color: var(--text-gray);
    margin-bottom: 3rem;
    font-size: 1.1rem;
}

.faq-grid {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.faq-item {
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px solid rgba(212, 175, 55, 0.1);
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
}

.faq-item:hover {
    border-color: var(--accent-gold);
    box-shadow: 0 5px 20px rgba(212, 175, 55, 0.15);
}

.faq-item.open {
    border-color: var(--accent-gold);
}

.faq-question {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    gap: 1rem;
}

.question-content {
    flex: 1;
}

.faq-category {
    display: inline-block;
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.faq-question h4 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-light);
    margin: 0;
}

.faq-question i {
    color: var(--accent-gold);
    transition: transform 0.3s ease;
    font-size: 1.2rem;
    flex-shrink: 0;
}

.faq-item.open .faq-question i {
    transform: rotate(180deg);
}

.faq-answer {
    padding: 0 2rem 1.5rem;
}

.faq-answer >>> p {
    color: var(--text-gray);
    line-height: 1.8;
    margin: 0 0 1rem 0;
}

.faq-answer >>> ul,
.faq-answer >>> ol {
    color: var(--text-gray);
    line-height: 1.8;
    padding-left: 1.5rem;
    margin: 0 0 1rem 0;
}

.faq-answer >>> li {
    margin-bottom: 0.5rem;
}

.faq-answer >>> strong {
    color: var(--accent-gold);
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* LOADING & EMPTY STATES */
.loading,
.no-faqs {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--text-gray);
}

.loading .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.no-faqs i {
    font-size: 4rem;
    color: rgba(212, 175, 55, 0.3);
    margin-bottom: 1rem;
}

/* RESOURCES */
.resources-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
}

.resource-card {
    background: var(--card-bg);
    padding: 2.5rem;
    border-radius: 16px;
    border: 1px solid rgba(212, 175, 55, 0.1);
    text-decoration: none;
    color: inherit;
    transition: all 0.3s ease;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.resource-card:hover {
    transform: translateY(-5px);
    border-color: var(--accent-gold);
    box-shadow: 0 10px 40px rgba(212, 175, 55, 0.2);
}

.resource-icon {
    width: 80px;
    height: 80px;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: var(--primary-dark);
    transition: transform 0.3s ease;
}

.resource-card:hover .resource-icon {
    transform: scale(1.1);
}

.resource-card h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 0.75rem;
}

.resource-card p {
    color: var(--text-gray);
    line-height: 1.6;
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .page-title {
        font-size: 2rem;
    }
    
    .section-title {
        font-size: 2rem;
    }
    
    .contact-cards {
        grid-template-columns: 1fr;
    }
    
    .faq-question {
        padding: 1rem 1.5rem;
    }
    
    .faq-answer {
        padding: 0 1.5rem 1rem;
    }
}
</style>